import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  Backdrop,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import HistoryIcon from "@mui/icons-material/History";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TableEdit from "../TableEdit";
import RowActionsMenu from "../RowActionsMenu";
import { dbFirebase } from "../../Conections/Firebase";
import { fetchCollection, fetchWhere } from "../../Utils/firestore";
import { computeDeudoresConEstado } from "../../Utils/deudorUtils";
import { readFileAsBase64 } from "../../Utils/fileUtils";
import DeudorFormDialog from "./DeudorFormDialog";
import NavbarProject from "../NavbarProject";
import { formatearNumero } from "../../Utils/formatUtils";
import { formateoFechas, getCurrentDateTime } from "../../Utils/dateUtils";
import Swal from "sweetalert2";

// Cuenta raíz de capital desde donde se financian los préstamos a deudores
const CAPITAL_ROOT_ID = "fPtXxA3nNBSbFga44yBA";

const initialPagoForm = {
  destino: "",
  cantidad: "",
  fecha: getCurrentDateTime(),
  descripcion: "",
};

const Deudores = () => {
  const [deudores, setDeudores] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [open, setOpen] = useState(false);
  const [dialogToken, setDialogToken] = useState(false);
  const [urlGenerada, setUrlGenerada] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogNuevoPrestamo, setDialogNuevoPrestamo] = useState(false);
  const [deudorSeleccionado, setDeudorSeleccionado] = useState(null);
  const [nuevoPrestamo, setNuevoPrestamo] = useState({
    totalDeuda: "",
  });

  const [dialogPago, setDialogPago] = useState({ open: false, deudor: null });
  const [pagoForm, setPagoForm] = useState(initialPagoForm);
  const [pagoFile, setPagoFile] = useState(null);
  const [dialogHistorial, setDialogHistorial] = useState({ open: false, deudor: null });
  const [dialogComprobante, setDialogComprobante] = useState({ open: false, base64: null });

  const getDeudores = async () => {
    setLoading(true);
    try {
      const [deudoresData, prestamosData, tokensData, destinosData] = await Promise.all([
        fetchCollection("deudores"),
        fetchCollection("prestamos"),
        fetchCollection("tokensTemporales"),
        fetchCollection("lista_inversion"),
      ]);

      setDestinos(destinosData);

      const ahora = new Date();

      const tokensMap = {};
      tokensData.forEach((tokenDoc) => {
        if (tokenDoc.expiresAt.toDate() > ahora) {
          const current = tokensMap[tokenDoc.deudorId];
          if (
            !current ||
            (tokenDoc.numeroPrestamo || 0) >= (current.numeroPrestamo || 0)
          ) {
            tokensMap[tokenDoc.deudorId] = {
              id: tokenDoc.id,
              numeroPrestamo: tokenDoc.numeroPrestamo || 0,
            };
          }
        }
      });

      const deudoresConEstado = computeDeudoresConEstado(deudoresData, prestamosData);

      const data = deudoresConEstado.map((deudorData) => {
        const tokenInfo = tokensMap[deudorData.id];
        const tokenVigente =
          tokenInfo && tokenInfo.numeroPrestamo === deudorData.numeroPrestamoActual
            ? tokenInfo.id
            : null;

        return { ...deudorData, tokenVigente };
      });

      setDeudores(data);
    } finally {
      setLoading(false);
    }
  };

  const limpiarTokensDeudor = async (deudorId) => {
    const tokens = await fetchWhere("tokensTemporales", "deudorId", "==", deudorId);

    for (const token of tokens) {
      await deleteDoc(doc(dbFirebase, "tokensTemporales", token.id));
    }
  };

  const generarTokenTemporal = async (deudor) => {
    const ahora = new Date();
    const numeroPrestamo = Number(deudor.numeroPrestamoActual) || 1;
    const fechaInicioPrestamo =
      deudor.fechaInicioPrestamoActual || getCurrentDateTime();

    const tokens = await fetchWhere("tokensTemporales", "deudorId", "==", deudor.id);

    for (const token of tokens) {
      const expiresAt = token.expiresAt.toDate();
      const mismoPrestamo = (token.numeroPrestamo || 1) === numeroPrestamo;

      if (expiresAt > ahora && mismoPrestamo) {
        return {
          url: `${window.location.protocol}//${window.location.host}/consulta/deuda/${token.id}`,
          tokenId: token.id,
        };
      }

      await deleteDoc(doc(dbFirebase, "tokensTemporales", token.id));
    }

    const token = crypto.randomUUID();
    const expiracion = Timestamp.fromDate(
      new Date(ahora.getTime() + 24 * 60 * 60 * 1000)
    );

    await setDoc(doc(dbFirebase, "tokensTemporales", token), {
      deudorId: deudor.id,
      numeroPrestamo,
      fechaInicioPrestamo,
      expiresAt: expiracion,
    });

    return {
      url: `${window.location.protocol}//${window.location.host}/consulta/deuda/${token}`,
      tokenId: token,
    };
  };

  const handleGenerarToken = async (deudor) => {
    try {
      if (!deudor.prestamoActivo) {
        Swal.fire(
          "Sin préstamo activo",
          "Primero debes abrir un préstamo nuevo para este deudor.",
          "warning"
        );
        return;
      }

      setLoading(true);
      const { url } = await generarTokenTemporal(deudor);
      setUrlGenerada(url);
      setCopiado(false);
      setDialogToken(true);
      await getDeudores();
    } catch (err) {
      console.error(err);
      alert("Error generando token");
    } finally {
      setLoading(false);
    }
  };

  const handleCopiarToken = async (tokenId) => {
    const url = `${window.location.protocol}//${window.location.host}/consulta/deuda/${tokenId}`;
    try {
      await navigator.clipboard.writeText(url);
      setUrlGenerada(url);
      setCopiado(true);
      setDialogToken(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      alert("No se pudo copiar el enlace");
    }
  };

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(urlGenerada);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      alert("No se pudo copiar el enlace");
    }
  };

  const handleLiquidarPrestamo = async (deudor) => {
    if (!deudor.prestamoActivo) {
      return;
    }

    if (deudor.restante > 0) {
      Swal.fire(
        "No se puede liquidar",
        "El deudor todavía tiene saldo pendiente en su préstamo actual.",
        "warning"
      );
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Marcar préstamo como liquidado",
      text: "Esto cerrará el préstamo actual y evitará que se sigan mostrando sus pagos en futuros tokens.",
      showCancelButton: true,
      confirmButtonText: "Liquidar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      await updateDoc(doc(dbFirebase, "deudores", deudor.id), {
        prestamoActivo: false,
        prestamoLiquidadoAt: getCurrentDateTime(),
        totalDeuda: 0,
      });
      await limpiarTokensDeudor(deudor.id);
      await getDeudores();
      Swal.fire("Actualizado", "El préstamo actual quedó liquidado.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo liquidar el préstamo: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogNuevoPrestamo = (deudor) => {
    if (deudor.prestamoActivo) {
      Swal.fire(
        "Préstamo activo",
        "Primero liquida el préstamo actual antes de abrir uno nuevo.",
        "warning"
      );
      return;
    }

    setDeudorSeleccionado(deudor);
    setNuevoPrestamo({ totalDeuda: "" });
    setDialogNuevoPrestamo(true);
  };

  const handleCrearNuevoPrestamo = async () => {
    const monto = Number(nuevoPrestamo.totalDeuda);

    if (!deudorSeleccionado || monto <= 0) {
      Swal.fire("Monto inválido", "Captura un monto válido para el nuevo préstamo.", "warning");
      return;
    }

    try {
      setLoading(true);

      await updateDoc(doc(dbFirebase, "deudores", deudorSeleccionado.id), {
        totalDeuda: monto,
        prestamoActivo: true,
        prestamoLiquidadoAt: null,
        fechaInicioPrestamoActual: getCurrentDateTime(),
        numeroPrestamoActual:
          (Number(deudorSeleccionado.numeroPrestamoActual) || 0) + 1,
      });

      await limpiarTokensDeudor(deudorSeleccionado.id);
      setDialogNuevoPrestamo(false);
      setDeudorSeleccionado(null);
      setNuevoPrestamo({ totalDeuda: "" });
      await getDeudores();

      Swal.fire("Préstamo creado", "El deudor ya tiene un nuevo préstamo activo.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo crear el nuevo préstamo: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogPago = (deudor) => {
    if (!deudor.prestamoActivo) {
      Swal.fire(
        "Sin préstamo activo",
        "Este deudor no tiene un préstamo activo para registrar pagos.",
        "warning"
      );
      return;
    }

    setDialogPago({ open: true, deudor });
    setPagoForm(initialPagoForm);
    setPagoFile(null);
  };

  const handleRegistrarPago = async () => {
    const deudor = dialogPago.deudor;
    const monto = Number(pagoForm.cantidad);

    if (!pagoForm.destino || monto <= 0) {
      Swal.fire("Faltan datos", "Selecciona un destino y una cantidad válida.", "warning");
      return;
    }

    try {
      setLoading(true);

      let comprobanteId = null;
      const base64 = await readFileAsBase64(pagoFile);
      if (base64) {
        const compDoc = await addDoc(collection(dbFirebase, "comprobantes"), {
          deudorId: deudor.id,
          fecha: pagoForm.fecha,
          base64,
        });
        comprobanteId = compDoc.id;
      }

      await addDoc(collection(dbFirebase, "prestamos"), {
        deudor: deudor.id,
        destino: pagoForm.destino,
        cantidad: monto,
        fecha: pagoForm.fecha,
        descripcion: pagoForm.descripcion,
        comprobanteId,
        numeroPrestamo: deudor.numeroPrestamoActual || 1,
        fechaInicioPrestamo: deudor.fechaInicioPrestamoActual || null,
      });

      await addDoc(collection(dbFirebase, "inversiones"), {
        fecha: pagoForm.fecha,
        cantidad: monto,
        raiz: CAPITAL_ROOT_ID,
        tipo: "EGRESO",
      });

      await addDoc(collection(dbFirebase, "inversiones"), {
        fecha: pagoForm.fecha,
        cantidad: monto,
        raiz: pagoForm.destino,
        tipo: "INGRESO",
      });

      setDialogPago({ open: false, deudor: null });
      await getDeudores();

      Swal.fire("Registro exitoso", "El pago fue registrado correctamente.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo registrar el pago: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const verComprobante = async (comprobanteId) => {
    try {
      const docSnap = await getDoc(doc(dbFirebase, "comprobantes", comprobanteId));
      if (docSnap.exists()) {
        setDialogComprobante({ open: true, base64: docSnap.data().base64 });
      } else {
        Swal.fire("No encontrado", "No se encontró el comprobante", "warning");
      }
    } catch (error) {
      Swal.fire("Error", "No se pudo cargar el comprobante: " + error.message, "error");
    }
  };

  const historialColumns = [
    { key: "fecha", header: "Fecha", render: (value) => formateoFechas(value) },
    { key: "cantidad", header: "Cantidad", render: (value) => formatearNumero(value) },
    { key: "descripcion", header: "Descripción" },
    {
      key: "comprobanteId",
      header: "Comprobante",
      render: (value) =>
        value ? (
          <Button size="small" onClick={() => verComprobante(value)}>
            Ver
          </Button>
        ) : (
          "Sin archivo"
        ),
    },
  ];

  const columns = [
    { key: "nombre", header: "Nombre" },
    {
      key: "totalDeuda",
      header: "Total de Deuda",
      render: (value) => `${formatearNumero(value) ?? formatearNumero(0)}`,
    },
    {
      key: "restante",
      header: "Restante de Deuda",
      render: (value) => `${formatearNumero(value) ?? formatearNumero(0)}`,
    },
    {
      key: "estadoPrestamo",
      header: "Estado",
      render: (_, row) =>
        row.prestamoActivo ? (
          <Chip label={`Activo #${row.numeroPrestamoActual}`} color="warning" size="small" />
        ) : (
          <Chip label="Liquidado" color="success" size="small" />
        ),
    },
    {
      key: "acciones",
      header: "Acción",
      render: (_, row) => {
        const secundarias = [
          row.prestamoActivo
            ? row.tokenVigente
              ? {
                  label: "Copiar token",
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: () => handleCopiarToken(row.tokenVigente),
                  disabled: loading,
                }
              : {
                  label: "Generar token",
                  icon: <VpnKeyIcon fontSize="small" />,
                  onClick: () => handleGenerarToken(row),
                  disabled: loading,
                }
            : null,
          {
            label: `Ver pagos (${row.pagos.length})`,
            icon: <HistoryIcon fontSize="small" />,
            onClick: () => setDialogHistorial({ open: true, deudor: row }),
            disabled: loading || row.pagos.length === 0,
          },
          row.prestamoActivo
            ? {
                label: "Marcar préstamo liquidado",
                icon: <TaskAltIcon fontSize="small" />,
                onClick: () => handleLiquidarPrestamo(row),
                disabled: loading,
                color: "warning",
              }
            : {
                label: "Nuevo préstamo",
                icon: <AccountBalanceWalletIcon fontSize="small" />,
                onClick: () => abrirDialogNuevoPrestamo(row),
                disabled: loading,
              },
        ];

        return (
          <RowActionsMenu
            primary={{
              label: "Registrar pago",
              onClick: () => abrirDialogPago(row),
              disabled: loading || !row.prestamoActivo,
            }}
            actions={secundarias}
          />
        );
      },
    },
  ];

  useEffect(() => {
    getDeudores();
  }, []);

  const resumenDeudores = useMemo(() => {
    const activos = deudores.filter((d) => d.prestamoActivo);
    return {
      total: deudores.length,
      activos: activos.length,
      carteraPorCobrar: activos.reduce((acc, d) => acc + (d.restante || 0), 0),
    };
  }, [deudores]);

  return (
    <>
      <NavbarProject />
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5">Gestión de Deudores</Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Deudores registrados
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {resumenDeudores.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Préstamos activos
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {resumenDeudores.activos}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Cartera por cobrar
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {formatearNumero(resumenDeudores.carteraPorCobrar)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
            <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
              disabled={loading}
          >
            Agregar deudor
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TableEdit columns={columns} rows={deudores} />
        </Grid>

        <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Registrar nuevo deudor</DialogTitle>
          <DialogContent>
            <DeudorFormDialog
              onSuccess={() => {
                setOpen(false);
                getDeudores();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialogToken} onClose={() => setDialogToken(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Enlace temporal</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              value={urlGenerada}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopiar} edge="end">
                      {copiado ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogToken(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialogNuevoPrestamo}
          onClose={() => setDialogNuevoPrestamo(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Nuevo préstamo</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {deudorSeleccionado?.nombre}
            </Typography>
            <TextField
              fullWidth
              label="Monto del nuevo préstamo"
              type="number"
              sx={{ mt: 2 }}
              value={nuevoPrestamo.totalDeuda}
              onChange={(e) =>
                setNuevoPrestamo((current) => ({
                  ...current,
                  totalDeuda: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogNuevoPrestamo(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleCrearNuevoPrestamo}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialogPago.open}
          onClose={() => setDialogPago({ open: false, deudor: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Registrar pago</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {dialogPago.deudor?.nombre}
            </Typography>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Destino (a dónde cayó)</InputLabel>
              <Select
                value={pagoForm.destino}
                label="Destino"
                onChange={(e) => setPagoForm((p) => ({ ...p, destino: e.target.value }))}
              >
                {destinos
                  .filter((d) => d.activo !== false)
                  .map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.nombre}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Cantidad"
              type="number"
              sx={{ mt: 2 }}
              value={pagoForm.cantidad}
              onChange={(e) => setPagoForm((p) => ({ ...p, cantidad: e.target.value }))}
            />

            <TextField
              fullWidth
              label="Fecha"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
              value={pagoForm.fecha}
              onChange={(e) => setPagoForm((p) => ({ ...p, fecha: e.target.value }))}
            />

            <TextField
              fullWidth
              label="Descripción"
              sx={{ mt: 2 }}
              multiline
              rows={2}
              value={pagoForm.descripcion}
              onChange={(e) => setPagoForm((p) => ({ ...p, descripcion: e.target.value }))}
            />

            <TextField
              fullWidth
              label="Comprobante (PDF o imagen)"
              type="file"
              InputLabelProps={{ shrink: true }}
              inputProps={{ accept: ".pdf,image/*" }}
              sx={{ mt: 2 }}
              onChange={(e) => setPagoFile(e.target.files[0])}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogPago({ open: false, deudor: null })}>Cancelar</Button>
            <Button variant="contained" onClick={handleRegistrarPago}>
              Registrar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialogHistorial.open}
          onClose={() => setDialogHistorial({ open: false, deudor: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Historial de pagos — {dialogHistorial.deudor?.nombre}</DialogTitle>
          <DialogContent>
            <TableEdit columns={historialColumns} rows={dialogHistorial.deudor?.pagos || []} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogHistorial({ open: false, deudor: null })}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialogComprobante.open}
          onClose={() => setDialogComprobante({ open: false, base64: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Comprobante</DialogTitle>
          <DialogContent>
            {dialogComprobante.base64?.includes("application/pdf") ? (
              <iframe
                title="Comprobante PDF"
                src={dialogComprobante.base64}
                style={{ width: "100%", height: "600px" }}
              ></iframe>
            ) : (
              <img
                src={dialogComprobante.base64}
                alt="Comprobante"
                style={{
                  width: "100%",
                  maxHeight: "600px",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogComprobante({ open: false, base64: null })}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};

export default Deudores;
